import React, { useEffect, useState } from "react";
import { 
  Image, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Upload,
  X,
  Save,
  Calendar,
  MapPin,
  Users,
  ArrowUp,
  ArrowDown
} from "react-feather";
import { 
  collection, 
  getDocs, 
  doc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  orderBy
} from "firebase/firestore";
import { db, uploadImage } from "shared/utils/firebase";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./GalleryManagement.module.css";

function GalleryManagement() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);

  const [itemForm, setItemForm] = useState({
    image: "",
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    category: "Technology",
    attendees: "",
    isEvent: true,
    tags: [],
    order: 0
  });

  const categories = ["Technology", "Workshop", "Research", "Networking", "Exhibition", "Symposium"];

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [galleryItems, searchTerm]);

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const itemsSnapshot = await getDocs(
        query(collection(db, "gallery"), orderBy("order", "asc"))
      );
      const itemsData = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      setGalleryItems(itemsData);
    } catch (error) {
      console.error("Error fetching gallery items:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = galleryItems;

    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredItems(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItemForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setItemForm(prev => ({ ...prev, tags }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    uploadImage(
      file,
      (progress) => {
        setImageProgress(progress);
      },
      (url) => {
        setItemForm(prev => ({ ...prev, image: url }));
        setUploadingImage(false);
        setImageProgress(0);
      },
      (error) => {
        console.error("Image upload error:", error);
        setUploadingImage(false);
        setImageProgress(0);
      }
    );
  };

  const handleOrderChange = async (itemId, direction) => {
    const item = galleryItems.find(i => i.id === itemId);
    if (!item) return;

    const currentIndex = galleryItems.findIndex(i => i.id === itemId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= galleryItems.length) return;

    const newOrder = galleryItems[newIndex].order;
    const oldOrder = item.order;

    try {
      await updateDoc(doc(db, "gallery", itemId), { order: newOrder });
      await updateDoc(doc(db, "gallery", galleryItems[newIndex].id), { order: oldOrder });
      
      const updated = [...galleryItems];
      [updated[currentIndex], updated[newIndex]] = [updated[newIndex], updated[currentIndex]];
      setGalleryItems(updated);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const itemData = {
        ...itemForm,
        attendees: parseInt(itemForm.attendees) || 0,
        order: editingItem ? editingItem.order : galleryItems.length,
        createdAt: editingItem ? editingItem.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingItem) {
        await updateDoc(doc(db, "gallery", editingItem.id), itemData);
        setGalleryItems(galleryItems.map(item => 
          item.id === editingItem.id ? { ...item, ...itemData } : item
        ));
      } else {
        const docRef = await addDoc(collection(db, "gallery"), itemData);
        setGalleryItems([...galleryItems, { id: docRef.id, ...itemData }]);
      }

      setShowItemModal(false);
      setEditingItem(null);
      resetForm();
    } catch (error) {
      console.error("Error saving gallery item:", error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setItemForm({
      image: item.image || "",
      title: item.title || "",
      description: item.description || "",
      date: item.date || "",
      time: item.time || "",
      location: item.location || "",
      category: item.category || "Technology",
      attendees: item.attendees || "",
      isEvent: item.isEvent !== undefined ? item.isEvent : true,
      tags: item.tags || [],
      order: item.order || 0
    });
    setShowItemModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteDoc(doc(db, "gallery", itemToDelete.id));
      setGalleryItems(galleryItems.filter(item => item.id !== itemToDelete.id));
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting gallery item:", error);
    }
  };

  const resetForm = () => {
    setItemForm({
      image: "",
      title: "",
      description: "",
      date: "",
      time: "",
      location: "",
      category: "Technology",
      attendees: "",
      isEvent: true,
      tags: [],
      order: 0
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading gallery items...</p>
      </div>
    );
  }

  return (
    <div className={styles.galleryManagement}>
      <PageHeader
        icon={Image}
        title="Gallery & Media Asset Management"
        subtitle="Manage home banner carousel images, campus gallery photos, and media assets"
        badge={`${filteredItems.length} Media Assets`}
        actions={
          <button 
            className={styles.addBtn}
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowItemModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Add Media Asset
          </button>
        }
      />

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search gallery items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{galleryItems.length}</div>
          <div className={styles.statLabel}>Total Items</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {galleryItems.filter(i => i.isEvent).length}
          </div>
          <div className={styles.statLabel}>Events</div>
        </div>
      </div>

      <div className={styles.galleryGrid}>
        {filteredItems.map((item, index) => (
          <div key={item.id} className={styles.galleryCard}>
            <div className={styles.galleryImageContainer}>
              <img 
                src={item.image || "/slider_img/default.jpg"} 
                alt={item.title}
                className={styles.galleryImage}
                onError={(e) => {
                  e.target.src = "/slider_img/default.jpg";
                }}
              />
              <div className={styles.galleryOverlay}>
                <div className={styles.orderControls}>
                  {index > 0 && (
                    <button
                      className={styles.orderBtn}
                      onClick={() => handleOrderChange(item.id, 'up')}
                      title="Move Up"
                    >
                      <ArrowUp size={16} />
                    </button>
                  )}
                  {index < filteredItems.length - 1 && (
                    <button
                      className={styles.orderBtn}
                      onClick={() => handleOrderChange(item.id, 'down')}
                      title="Move Down"
                    >
                      <ArrowDown size={16} />
                    </button>
                  )}
                </div>
                <div className={styles.galleryActions}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => handleEdit(item)}
                    title="Edit Item"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={() => {
                      setItemToDelete(item);
                      setShowDeleteModal(true);
                    }}
                    title="Delete Item"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.galleryContent}>
              <h5 className={styles.galleryTitle}>{item.title}</h5>
              <p className={styles.galleryDescription}>{item.description}</p>
              <div className={styles.galleryDetails}>
                {item.date && (
                  <div className={styles.detailItem}>
                    <Calendar size={14} className="me-1" />
                    <span>{item.date}</span>
                  </div>
                )}
                {item.location && (
                  <div className={styles.detailItem}>
                    <MapPin size={14} className="me-1" />
                    <span>{item.location}</span>
                  </div>
                )}
                {item.attendees && (
                  <div className={styles.detailItem}>
                    <Users size={14} className="me-1" />
                    <span>{item.attendees} attendees</span>
                  </div>
                )}
              </div>
              <div className={styles.galleryMeta}>
                <span className="badge bg-primary">{item.category}</span>
                {item.isEvent && (
                  <span className="badge bg-success">Event</span>
                )}
                <span className="badge bg-secondary">Order: {item.order}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className={styles.emptyState}>
          <Image size={48} className="text-muted mb-3" />
          <h5>No gallery items found</h5>
          <p className="text-muted">
            {searchTerm 
              ? "Try adjusting your search"
              : "No gallery items have been added yet"
            }
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowItemModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Add First Gallery Item
          </button>
        </div>
      )}

      {/* Gallery Item Form Modal */}
      {showItemModal && (
        <div className={styles.modalOverlay} onClick={() => setShowItemModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>{editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowItemModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={itemForm.title}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Category *</label>
                  <select
                    name="category"
                    value={itemForm.category}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={itemForm.date}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Time</label>
                  <input
                    type="text"
                    name="time"
                    value={itemForm.time}
                    onChange={handleInputChange}
                    placeholder="e.g., 10:00 AM - 4:00 PM"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={itemForm.location}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Attendees</label>
                  <input
                    type="number"
                    name="attendees"
                    value={itemForm.attendees}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Description *</label>
                <textarea
                  name="description"
                  value={itemForm.description}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Tags (comma-separated)</label>
                <input
                  type="text"
                  value={itemForm.tags.join(', ')}
                  onChange={handleTagsChange}
                  placeholder="e.g., AI, IoT, VLSI, Innovation"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Image *</label>
                <div className={styles.imageUpload}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={styles.fileInput}
                    id="gallery-image"
                  />
                  <label htmlFor="gallery-image" className={styles.fileLabel}>
                    <Upload size={16} className="me-2" />
                    Choose Image
                  </label>
                  {uploadingImage && (
                    <div className={styles.uploadProgress}>
                      <div className="progress">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${imageProgress}%` }}
                        />
                      </div>
                      <small>{imageProgress.toFixed(1)}% uploaded</small>
                    </div>
                  )}
                  {itemForm.image && (
                    <div className={styles.imagePreview}>
                      <img src={itemForm.image} alt="Gallery preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isEvent"
                    checked={itemForm.isEvent}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span>Is Event</span>
                </label>
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowItemModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <Save size={16} className="me-2" />
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Confirm Deletion</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete <strong>"{itemToDelete.title}"</strong>?
              </p>
              <p className="text-danger">
                This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryManagement;

